// CustomizeColumns.jsx
import { useState, useEffect, useRef } from "react";
import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  IconButton,
  Box,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { tokens, withAlpha } from "../Common/designTokens"; // added
import { applyRenderFunctions } from "./OrderComponents/columnRendererRegistry";
import { clearConfigIfOutdated, updateColumnConfigIfNeeded } from "../Utils/columnVersionManager"; // Import the utility
import { getPreference, setPreference } from "../Utils/userPreferenceManger"; // Import user preference manager

// Styled components (unchanged)

const StyledListItem = styled(ListItem)(({ theme }) => ({
  backgroundColor: tokens.colors.defaultGridRowColor,
  height: '40px',
  borderRadius: '8px',
  "&:hover": {
    backgroundColor: tokens.colors.defaultGridHoverColor,
  },
}));


function CustomizeColumns({
  open,
  onClose,
  allColumns,
  selectedColumns: initialSelectedColumns,
  setSelectedColumnsInventory,
  anchorEl,
  sectionKey,
  source = null,
  height = 460, // Add default height parameter
  fixedColumnKeys = [], // Column keys that cannot be unchecked/removed
}) {
  const fixedKeySet = new Set(fixedColumnKeys);
  // Track the full order of all columns (selected and unselected)
  const [orderedColumns, setOrderedColumns] = useState([]);
  const [checkedColumns, setCheckedColumns] = useState([]);
  const dialogRef = useRef(null);

  // Check column version and update if needed when component mounts
  useEffect(() => {
    if (allColumns && sectionKey) {
      // Update column configuration if needed before initializing
      clearConfigIfOutdated(sectionKey, allColumns);
    }
  }, [sectionKey, allColumns]);

  // Initialize columns and checked state when dialog opens
  useEffect(() => {
    if (open) {
      try {
        // Check if fullColumnOrder exists for this section using role-based preferences
        const savedFullOrder = getPreference(`fullColumnOrder_${sectionKey}`);
        
        // If fullColumnOrder doesn't exist, initialize it with allColumns
        if (!savedFullOrder) {
          setPreference(`fullColumnOrder_${sectionKey}`, allColumns);
          console.log(`Initialized fullColumnOrder_${sectionKey} with all columns`);
        }

        // Get all relevant saved orders using role-based preferences
        const savedColumnOrder = getPreference(`columnOrder_${sectionKey}`);
        const savedSelectedColumns = getPreference(`selectedColumns_${sectionKey}`);
        
        // Initialize ordered columns
        let currentOrderedColumns = [];

        // Resolve every column's presentation metadata (name, width, sortable, etc.)
        // from the current code-defined allColumns by key. Storage is only trusted
        // for ORDER and SELECTION, never for content — otherwise a renamed label
        // (e.g. "Security Charge" -> "Factored Securities") stays stale until a
        // version bump forces a reset.
        const columnsByKey = new Map(allColumns.map((col) => [col.key, col]));

        if (savedFullOrder) {
          try {
            const parsedFullOrder = savedFullOrder;
            if (Array.isArray(parsedFullOrder)) {
              // Make sure first column stays at first position if it exists in parsedFullOrder
              const firstColumn = allColumns[0];
              const remainingColumns = parsedFullOrder
                // Re-resolve against allColumns by key: refreshes name/width/etc.
                // and drops any saved column that no longer exists in the code.
                .map((col) => columnsByKey.get(col?.key))
                .filter(Boolean)
                .filter((col) => col.key !== firstColumn.key);

              // Add any missing columns from allColumns to the end
              const fullOrderKeys = new Set(remainingColumns.map(col => col.key));
              const missingColumns = allColumns
                .slice(1) // Skip first column since we handle it separately
                .filter(col => !fullOrderKeys.has(col.key));

              currentOrderedColumns = [firstColumn, ...remainingColumns, ...missingColumns];
            }
          } catch (e) {
            console.error("Error parsing savedFullOrder:", e);
          }
        }
        
        // If no valid saved full order, use allColumns
        if (currentOrderedColumns.length === 0) {
          currentOrderedColumns = allColumns;
        }
        
        // Set the ordered columns
        setOrderedColumns(currentOrderedColumns);
        
        // Initialize checked columns from saved selected columns
        let currentSelectedColumns = [];
        if (savedSelectedColumns && Array.isArray(savedSelectedColumns)) {
          currentSelectedColumns = savedSelectedColumns;
        }
        
        // If no valid saved selection, use initialSelectedColumns
        if (currentSelectedColumns.length === 0) {
          currentSelectedColumns = initialSelectedColumns || [];
        }
        
        // Set checked columns based on current selection, ensuring fixed columns are always checked
        const checkedKeys = new Set(currentSelectedColumns.map(col => col.key));
        fixedColumnKeys.forEach(key => checkedKeys.add(key));
        setCheckedColumns([...checkedKeys]);

      } catch (e) {
        console.error("Error in CustomizeColumns initialization:", e);
        // Fallback to initial values and ensure fullColumnOrder exists
        setPreference(`fullColumnOrder_${sectionKey}`, allColumns);
        setOrderedColumns(allColumns);
        const fallbackKeys = new Set(initialSelectedColumns?.map(col => col.key) || []);
        fixedColumnKeys.forEach(key => fallbackKeys.add(key));
        setCheckedColumns([...fallbackKeys]);
      }
    }
  }, [open, allColumns, initialSelectedColumns, sectionKey]);

  // Add new useEffect for initialization when component mounts
  useEffect(() => {
    // Check if fullColumnOrder exists for this section using role-based preferences
    const savedFullOrder = getPreference(`fullColumnOrder_${sectionKey}`);
    const savedSelectedColumns = getPreference(`selectedColumns_${sectionKey}`);
    const defaultSelectedColumns = initialSelectedColumns || [];
    
    // Initialize both if they don't exist
    if (!savedFullOrder && !savedSelectedColumns && allColumns) {
      setPreference(`fullColumnOrder_${sectionKey}`, allColumns);
      setPreference(`selectedColumns_${sectionKey}`, defaultSelectedColumns);
      setPreference(`columnOrder_${sectionKey}`, defaultSelectedColumns);
      // Set the initial version for this section using role-based preferences
      const currentVersion = getPreference(`columnConfigVersion_${sectionKey}`, '1');
      setPreference(`columnConfigVersion_${sectionKey}`, currentVersion);
      console.log(`Initialized both fullColumnOrder and selectedColumns for ${sectionKey}`);
      
      // Update the UI with the initialized columns
      const columnsWithRenders = applyRenderFunctions(defaultSelectedColumns, source, sectionKey);
      setSelectedColumnsInventory(columnsWithRenders);
    }
  }, [sectionKey, allColumns, initialSelectedColumns, source, setSelectedColumnsInventory]); // Run when sectionKey, allColumns, or source changes

  const handleToggle = (columnKey) => {
    if (fixedKeySet.has(columnKey)) return; // Fixed columns cannot be toggled
    setCheckedColumns((prev) =>
      prev.includes(columnKey)
        ? prev.filter((key) => key !== columnKey)
        : [...prev, columnKey]
    );
  };

  const handleApply = () => {
    try {
      console.log(`CustomizeColumns: Applying columns with source=${source}, sectionKey=${sectionKey}`);
      
      // Get the current ordered columns in their current order
      const selected = orderedColumns.filter(col => checkedColumns.includes(col.key));
      
      // Apply render functions
      const selectedWithRenders = applyRenderFunctions(selected, source, sectionKey);
      
      // Save all relevant orders using role-based preferences
      setPreference(`selectedColumns_${sectionKey}`, selected);
      setPreference(`columnOrder_${sectionKey}`, selected);
      setPreference(`fullColumnOrder_${sectionKey}`, orderedColumns);
      
      // Update the UI
      setSelectedColumnsInventory(selectedWithRenders);
      
      onClose();
    } catch (e) {
      console.error("Error in handleApply:", e);
    }
  };

  // Calculate position based on anchor element
  const getPosition = () => {
    if (anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      const dialogHeight = height; // Use the height prop
      const windowHeight = window.innerHeight;
      
      // Calculate initial position
      let top = rect.bottom + window.scrollY;
      let left = rect.right - 240; // Dialog width is 240px
      
      // Adjust if it would go below viewport
      if (top + dialogHeight > windowHeight) {
        top = rect.top - dialogHeight;
      }
      
      // Adjust if it would go off-screen to the left
      if (left < 0) {
        left = rect.left;
      }
      
      return {
        top,
        left,
      };
    }
    return {};
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        ref: dialogRef,
        sx: {
          position: "fixed",
          ...getPosition(),
          width: "240px",
          height: `${height}px`,
          margin: 0,
          borderRadius: "8px",
          backgroundColor: tokens.colors.defaultGridRowColor,
          border: `1px solid ${tokens.colors.bodyBorderColor50}`,
          boxShadow: `0 8px 24px ${withAlpha('#000000', 0.12)}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 1.5,
          borderBottom: `1px solid ${tokens.colors.bodyBorderColor50}`,
          backgroundColor: tokens.colors.defaultGridRowColor,
          color: tokens.colors.fontColour
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="subtitle1">Customize Columns</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent 
        sx={{ 
          p: 0,
          overflowY: "auto",
          backgroundColor: tokens.colors.defaultGridRowColor
        }}
      >
        <List sx={{ py: 0 }}>
          {orderedColumns.map((column, index) => {
            const isFixed = fixedKeySet.has(column.key);
            return (
              <StyledListItem key={column.key}>
                <Checkbox
                  checked={checkedColumns.includes(column.key)}
                  onChange={() => handleToggle(column.key)}
                  edge="start"
                  tabIndex={-1}
                  disableRipple
                  disabled={isFixed}
                  size="small"
                  sx={isFixed ? {
                    opacity: 0.45,
                    cursor: 'not-allowed',
                    pointerEvents: 'none',
                  } : undefined}
                />
                <ListItemText
                  primary={`${index + 1}. ${column.name}`}
                  primaryTypographyProps={{
                    sx: {
                      fontFamily: 'Lato',
                      fontSize: '16px',
                      lineHeight: '18px',
                      letterSpacing: '0%',
                      ...(isFixed
                        ? {
                            fontWeight: 700,
                            color: tokens.colors.fontColour50 || withAlpha(tokens.colors.fontColour, 0.5),
                          }
                        : checkedColumns.includes(column.key)
                          ? {
                              fontWeight: 700,
                              color: tokens.colors.fontColour
                            }
                          : {
                              fontWeight: 400,
                              color: tokens.colors.fontColour85
                            }
                      )
                    }
                  }}
                />
              </StyledListItem>
            );
          })}
        </List>
      </DialogContent>

      <DialogActions
        sx={{
          p: 1.5,
          borderTop: `1px solid ${tokens.colors.bodyBorderColor50}`,
          justifyContent: "flex-end",
          gap: 1,
          backgroundColor: tokens.colors.defaultGridRowColor
        }}
      >
        <Button 
          onClick={onClose} 
          size="small"
          sx={{ color: tokens.colors.fontColour, borderRadius: "8px" }}
        >
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleApply} 
          size="small"
          sx={{
            backgroundColor: tokens.palette.primary,
            color: tokens.colors.white,
            borderRadius: "8px",
            '&:hover': {
              backgroundColor: tokens.palette.primaryA80,
            }
          }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CustomizeColumns;
